import React from "react";
import { useFetch } from "@/hooks/useApi";
import { downloadFile } from "@/lib/api";
import { PageHeader, Loader, Plate, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { gbp, num } from "@/lib/format";
import * as Icons from "lucide-react";
import { toast } from "sonner";

export default function Earnings() {
  const { data, loading } = useFetch("/earnings");
  if (loading || !data) return <Loader />;
  const totals = data.reduce((a, d) => ({ gross: a.gross + d.gross, ded: a.ded + d.deductions, net: a.net + d.net }), { gross: 0, ded: 0, net: 0 });
  const payslip = async (d) => {
    try { await downloadFile(`/payslips/${d.driver_id}`, `payslip_${d.driver_code}.pdf`); }
    catch { toast.error("Could not generate payslip"); }
  };

  return (
    <div className="fp-fade-in">
      <PageHeader title="Driver Earnings" subtitle="Approved stops × base rate × driver multiplier, less deductions" testId="earnings-page" />
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="p-5"><p className="text-xs uppercase tracking-wider text-muted-foreground">Total Gross</p><p className="font-mono text-2xl font-bold mt-2 text-emerald-400">{gbp(totals.gross)}</p></Card>
        <Card className="p-5"><p className="text-xs uppercase tracking-wider text-muted-foreground">Deductions</p><p className="font-mono text-2xl font-bold mt-2 text-rose-400">{gbp(totals.ded)}</p></Card>
        <Card className="p-5"><p className="text-xs uppercase tracking-wider text-muted-foreground">Total Net</p><p className="font-mono text-2xl font-bold mt-2 text-primary">{gbp(totals.net)}</p></Card>
      </div>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Driver</TableHead><TableHead>Van</TableHead><TableHead>Approved Stops</TableHead>
                <TableHead>Rate</TableHead><TableHead>Gross</TableHead><TableHead>Deductions</TableHead><TableHead>Net Income</TableHead><TableHead className="text-right">Payslip</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((d) => (
                <TableRow key={d.driver_id} data-testid={`earning-row-${d.driver_code}`}>
                  <TableCell><p className="font-medium">{d.name}</p><p className="text-xs text-muted-foreground font-mono">{d.driver_code}</p></TableCell>
                  <TableCell>{d.assigned_van_reg ? <Plate reg={d.assigned_van_reg} /> : "—"}</TableCell>
                  <TableCell className="font-mono">{num(d.approved_stops)}</TableCell>
                  <TableCell className="font-mono">{gbp(d.base_rate)} ×{d.rate_multiplier}</TableCell>
                  <TableCell className="font-mono text-emerald-400">{gbp(d.gross)}</TableCell>
                  <TableCell className="font-mono text-rose-400">{d.deductions ? `- ${gbp(d.deductions)}` : gbp(0)}</TableCell>
                  <TableCell className="font-mono font-bold text-primary">{gbp(d.net)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="outline" size="sm" onClick={() => payslip(d)} data-testid={`payslip-${d.driver_code}`}>
                      <Icons.FileDown className="h-4 w-4 mr-1.5" /> Weekly
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {data.length === 0 && <EmptyState icon="PoundSterling" title="No earnings yet" />}
      </Card>
    </div>
  );
}
